import {createAsyncThunk} from "@reduxjs/toolkit";
import type {User} from "@supabase/supabase-js";
import type {Dealership, DealerUser} from "@/types";
import {supabase} from "@/utils/supabase.ts";
import {resolveAuthenticatedDealerProfile, type AuthSuccessPayload} from "@/store/thunks/authenticationThunks/login.ts";
import {
    buildAuthMetadata,
    buildDealershipSeed,
    buildDealerUserSeed,
    generateId,
    keysToCamelCase,
    keysToSnakeCase,
    normalizeError,
    setAuthCookies
} from "@/utils";

type IndividualSignUpPayload = {
    accountType: "individual";
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
};

type CompanySignUpPayload = {
    accountType: "company";
    name: string;
    email: string;
    phone: string;
    password: string;
};

export type SignUpPayload = IndividualSignUpPayload | CompanySignUpPayload;

async function ensureDealerProfile(authUser: User, details: SignUpPayload) {
    const dealerUserResponse = await supabase
        .from("dealer_users")
        .select("*, dealer:dealerships(*)")
        .eq("id", authUser.id)
        .maybeSingle();

    if (dealerUserResponse.error) {
        throw new Error(dealerUserResponse.error.message);
    }

    if (dealerUserResponse.data) {
        return await resolveAuthenticatedDealerProfile(authUser);
    }

    const dealerId = generateId();

    const createdDealerResponse = await supabase
        .from("dealerships")
        .insert(
            keysToSnakeCase({
                ...buildDealershipSeed(details, authUser.id, authUser.email ?? details.email),
                id: dealerId
            })
        )
        .select("*")
        .single();

    if (createdDealerResponse.error) {
        throw new Error(createdDealerResponse.error.message);
    }

    const createdDealerUserResponse = await supabase
        .from("dealer_users")
        .insert(
            keysToSnakeCase({
                ...buildDealerUserSeed(details, authUser.email ?? details.email),
                id: authUser.id,
                dealerId
            })
        )
        .select("*")
        .single();

    if (createdDealerUserResponse.error) {
        throw new Error(createdDealerUserResponse.error.message);
    }

    return {
        user: keysToCamelCase<Dealership>(createdDealerResponse.data),
        dealerUser: keysToCamelCase<DealerUser>(createdDealerUserResponse.data),
        onboardingRequired: true
    };
}

export const signUpAsync = createAsyncThunk<AuthSuccessPayload, SignUpPayload, {rejectValue: string}>(
    "authentication/signUp",
    async (details, {rejectWithValue}) => {
        try {
            const authMetadata = buildAuthMetadata(details);

            const signUpAttempt = await supabase.auth.signUp({
                email: details.email,
                password: details.password,
                options: {
                    data: authMetadata
                }
            });

            let authUser = signUpAttempt.data.user;
            let session = signUpAttempt.data.session;

            if (signUpAttempt.error) {
                const errorMessage = signUpAttempt.error.message.toLowerCase();
                const isExistingUser = errorMessage.includes("already") || errorMessage.includes("registered");

                if (!isExistingUser) {
                    return rejectWithValue(signUpAttempt.error.message);
                }

                const signInAttempt = await supabase.auth.signInWithPassword({
                    email: details.email,
                    password: details.password
                });

                if (signInAttempt.error) {
                    return rejectWithValue(signInAttempt.error.message);
                }

                authUser = signInAttempt.data.user;
                session = signInAttempt.data.session;
            } else if (!signUpAttempt.data.session) {
                const signInAttempt = await supabase.auth.signInWithPassword({
                    email: details.email,
                    password: details.password
                });

                if (signInAttempt.error) {
                    return rejectWithValue(
                        "Account created. Verify your email, then sign in to complete dealer profile setup."
                    );
                }

                authUser = signInAttempt.data.user ?? authUser;
                session = signInAttempt.data.session ?? session;
            }

            if (!authUser) {
                return rejectWithValue("Unable to resolve authenticated user");
            }

            if (session) {
                setAuthCookies(session);
            }

            const authContext = await ensureDealerProfile(authUser, details);

            return {
                ...authContext,
                redirectTo: authContext.onboardingRequired ? "/wizard" : "/"
            };
        } catch (error: unknown) {
            return rejectWithValue(normalizeError(error, "Signup failed"));
        }
    }
);
